import { LegalDoc, LegalDocSet, resolveDocLang } from './legal-doc.model';
import { CHRONIQ_PRIVACY } from './chroniq/chroniq-privacy.content';
import { CHRONIQ_TERMS } from './chroniq/chroniq-terms.content';
import { SCREENDECK_PRIVACY } from './screendeck/screendeck-privacy.content';
import { SCREENDECK_TERMS } from './screendeck/screendeck-terms.content';

/**
 * Products with a short annex card on the base privacy/terms pages, keyed to i18n
 * keys `mainauth.<privacy|terms>.product.<product>.*`.
 */
export type AnnexProduct = 'tablestack' | 'menu';
export const ANNEX_PRODUCTS: readonly AnnexProduct[] = ['tablestack', 'menu'];

/** Products with a fully self-contained privacy/terms document of their own. */
export type StandaloneProduct = 'screendeck' | 'chroniq';
export const STANDALONE_PRODUCTS: readonly StandaloneProduct[] = ['screendeck', 'chroniq'];

/**
 * Privacy policies that stand on their own instead of annotating the group-wide text.
 *
 * ScreenDeck and Chroniq address entirely different audiences than the base policy
 * (passers-by scanning a QR code near a screen; employees of a customer), so a
 * self-contained notice per product is what those readers actually need.
 *
 * All product sections render on the SAME page as the base text (see
 * docs/KONZEPT-Produkt-Rechtsseiten.md) — there is deliberately no `?product=`
 * selector any more. A reader who arrives without the exact link, or via a copied
 * URL that lost its query string, used to fall back silently to the base-only
 * text; now nothing can be hidden that way, because every section is always there.
 */
const PRIVACY_DOCS: Record<StandaloneProduct, LegalDocSet> = {
    screendeck: SCREENDECK_PRIVACY,
    chroniq: CHRONIQ_PRIVACY,
};

/**
 * Product terms. Deliberately ANNEXES rather than standalone contracts: § 22 of the
 * base AGB treats product annexes as part of one agreement, and two parallel full
 * contracts with the same customer would contradict each other on remuneration,
 * liability and term. The base sections therefore keep rendering below every annex.
 */
const TERMS_DOCS: Record<StandaloneProduct, LegalDocSet> = {
    screendeck: SCREENDECK_TERMS,
    chroniq: CHRONIQ_TERMS,
};

/** Product names as shown in headings of the section index; proper names, identical in every language. */
export const PRODUCT_LABELS: Readonly<Record<StandaloneProduct | AnnexProduct, string>> = {
    chroniq: 'Chroniq',
    screendeck: 'ScreenDeck',
    tablestack: 'TableStack',
    menu: 'MenuLink',
};

/** Order of the product blocks in the section index of both legal pages. */
export const PRODUCT_TOC_ORDER: readonly (StandaloneProduct | AnnexProduct)[] = ['chroniq', 'screendeck', 'tablestack', 'menu'];

/** A standalone product's document, resolved to one language. */
export interface ResolvedProductDoc {
    key: StandaloneProduct;
    doc: LegalDoc;
}

export function allPrivacyDocs(requestedLang: string | null, sessionLang: string): readonly ResolvedProductDoc[] {
    return resolveAll(PRIVACY_DOCS, requestedLang, sessionLang);
}

export function allTermsDocs(requestedLang: string | null, sessionLang: string): readonly ResolvedProductDoc[] {
    return resolveAll(TERMS_DOCS, requestedLang, sessionLang);
}

function resolveAll(
    docs: Record<StandaloneProduct, LegalDocSet>,
    requestedLang: string | null,
    sessionLang: string,
): readonly ResolvedProductDoc[] {
    return STANDALONE_PRODUCTS
        .map((key): ResolvedProductDoc | null => {
            const set = docs[key];
            const doc = set[resolveDocLang(set, requestedLang, sessionLang)];
            return doc ? { key, doc } : null;
        })
        .filter((entry): entry is ResolvedProductDoc => entry !== null);
}
